import './PricingStep.css';
import { Icons, iconColors, type IconKey } from './Icons';

export interface PlanTier {
    id: 'baseline' | 'performance' | 'complete';
    name: string;
    price: number;
    description: string;
    targetAudience: string;
    featured?: boolean;
    originalPrice?: number;
    discountLabel?: string;
    mainFeatures: { icon: string; text: string; subtext?: string }[];
    includedFeatures: { icon: string; text: string; subtext?: string }[];
}



interface Feature {
    iconKey: IconKey;
    text: string;
    subtext?: string;
}

interface PlanData {
    id: 'baseline' | 'performance' | 'complete';
    name: string;
    price: number;
    description: string;
    targetAudience: string;
    featured?: boolean;
    originalPrice?: number;
    discountLabel?: string;
    mainFeatures: Feature[];
    includedFeatures: Feature[];
}

const plans: PlanData[] = [
    {
        id: 'baseline',
        name: 'Baseline',
        price: 999,
        originalPrice: 1499,
        discountLabel: 'Special Discount',
        description: 'who want to start their longevity journey.',
        targetAudience: 'Beginners',
        mainFeatures: [
            { iconKey: 'dna', text: '100+ labs and a full health report', subtext: 'A clear snapshot of your health today' },
            { iconKey: 'chart', text: 'AI powered Lab Data Analysis', subtext: 'AI-driven functional grading of 10 domains, toxin exposure risk, and disease prediction' },
            { iconKey: 'message', text: 'Audio Note (2 mins) from Doctor & Nutritionist', subtext: 'A personalized breakdown of your results' },
            { iconKey: 'pill', text: 'Personalised diet, supplement recommendation', subtext: 'Tailored to your unique biology' },
        ],
        includedFeatures: [
            { iconKey: 'folder', text: 'Consolidate past lab data instantly' },
            { iconKey: 'moon', text: 'Access to Nostavia Space Neural Lab' },
        ],
    },
    {
        id: 'performance',
        name: 'Pro',
        price: 4599,
        originalPrice: 5999,
        discountLabel: 'Limited Deal',
        description: 'who need more than just results, they need a science-backed strategy.',
        targetAudience: 'Optimizers',
        mainFeatures: [
            { iconKey: 'dna', text: '100+ labs and a full health report', subtext: 'A clear snapshot of your health today' },
            { iconKey: 'chart', text: 'Whole-Body Intelligence System', subtext: 'AI-driven functional grading of 10 domains, toxin exposure risk, and disease prediction' },
            { iconKey: 'clipboard', text: 'Deep-Dive Strategy Session', subtext: '45 mins with doctors & nutritionists who spend hours analyzing your data before they speak with you' },
            { iconKey: 'pill', text: 'Static Custom Protocol', subtext: 'Lifestyle, diet, fitness & supplements personalised on 200+ parameters' },
            { iconKey: 'sparkles', text: 'Nostavia AI Companion', subtext: 'Instant answers personalized to your labs, protocol & doctor\'s insights' },
        ],
        includedFeatures: [
            { iconKey: 'screen', text: 'Access to Member portal' },
            { iconKey: 'clipboard', text: 'Lifestyle, Medical History, & Family History Guided Assessments' },
            { iconKey: 'moon', text: 'Access to Nostavia Space Neural Lab' },
            { iconKey: 'folder', text: 'Consolidate past lab data instantly' },
        ],
    },
    {
        id: 'complete',
        name: 'Ultimate',
        price: 3999,
        originalPrice: 7499,
        discountLabel: 'Launch Special — Ends Soon',
        description: 'who need their health expertly managed so they can focus on everything else.',
        targetAudience: 'Elite Care',
        featured: true,
        mainFeatures: [
            { iconKey: 'dna', text: 'Biannual 100+ labs and a full health report', subtext: 'A clear snapshot of your health today' },
            { iconKey: 'chart', text: 'Whole-Body Intelligence System', subtext: 'All features of Pro, updated biannually to track progress' },
            { iconKey: 'clipboard', text: 'Quarterly Strategy Sessions', subtext: 'Continuous strategic adjustments with your Doctor as your body evolves' },
            { iconKey: 'clipboard', text: 'Fortnightly Nutritionist Session', subtext: 'Bi-weekly check-ins with updated nutrition plans' },
            { iconKey: 'pill', text: 'Dynamic Custom Protocol', subtext: 'Lifestyle, diet, fitness & supplements personalised on 200+ parameters (Evolves monthly)' },
            { iconKey: 'zap', text: '24/7 Medical Concierge', subtext: 'Unlimited access to your private Nutritionist and care team' },
            { iconKey: 'sparkles', text: 'Nostavia AI Companion', subtext: 'Instant answers personalized to your labs, protocol & doctor\'s insights' },
        ],
        includedFeatures: [
            { iconKey: 'screen', text: 'Access to Member portal' },
            { iconKey: 'clipboard', text: 'Lifestyle, Medical History, & Family History Guided Assessments' },
            { iconKey: 'moon', text: 'Access to Nostavia Space Neural Lab' },
            { iconKey: 'folder', text: 'Consolidate past lab data instantly' },
        ],
    },
];

// Convert to exportable format
const exportablePlans: PlanTier[] = plans.map(plan => ({
    ...plan,
    mainFeatures: plan.mainFeatures.map(f => ({ icon: f.iconKey, text: f.text, subtext: f.subtext })),
    includedFeatures: plan.includedFeatures.map(f => ({ icon: f.iconKey, text: f.text, subtext: f.subtext })),
}));

interface PricingStepProps {
    onSelectPlan: (plan: PlanTier) => void;
    onClose: () => void;
    isInternational?: boolean;
}

export function PricingStep({ onSelectPlan, onClose, isInternational = false }: PricingStepProps) {
    // International pricing in USD
    const getPrice = (planId: string, basePrice: number): { amount: number; display: string; currency: string } => {
        if (isInternational) {
            const usdPrices: Record<string, number> = { baseline: 59, performance: 99, complete: 79 };
            return { amount: usdPrices[planId] || 59, display: (usdPrices[planId] || 59).toString(), currency: '$' };
        }
        return { amount: basePrice, display: basePrice.toLocaleString('en-IN'), currency: '₹' };
    };

    // Mouse tracking for 3D tilt and shine effect
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        // Calculate rotation (max 15 degrees)
        const rotateX = ((y - centerY) / centerY) * -15;
        const rotateY = ((x - centerX) / centerX) * 15;

        // Calculate shine position
        const shineX = (x / rect.width) * 100;
        const shineY = (y / rect.height) * 100;

        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;

        // Update shine position
        const shine = card.querySelector('.card-shine') as HTMLElement;
        if (shine) {
            shine.style.background = `radial-gradient(circle at ${shineX}% ${shineY}%, rgba(255,255,255,0.4) 0%, transparent 60%)`;
            shine.style.opacity = '1';
        }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
        const card = e.currentTarget;
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';

        const shine = card.querySelector('.card-shine') as HTMLElement;
        if (shine) {
            shine.style.opacity = '0';
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <button className="close-btn" onClick={onClose} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
            <div className="pricing-modal modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="pricing-cards">
                    {plans.map((plan, index) => (
                        <div
                            key={plan.id}
                            className={`pricing-card ${plan.featured ? 'featured' : ''}`}
                            style={{ animationDelay: `${index * 0.1}s` }}
                        >
                            {plan.featured && <div className="featured-badge">Most Popular</div>}

                            <div
                                className="plan-image"
                                onMouseMove={handleMouseMove}
                                onMouseLeave={handleMouseLeave}
                            >
                                <img
                                    src={isInternational
                                        ? (plan.id === 'baseline' ? '/card-baseline-usd.png' : plan.id === 'performance' ? '/card-pro-usd.png' : '/card-ultimate-usd.png')
                                        : (plan.id === 'baseline' ? '/card-baseline.png' : plan.id === 'performance' ? '/card-pro.png' : '/card-ultimate.png')}
                                    alt={plan.name}
                                    className="plan-card-img"
                                />
                                <div className="card-shine"></div>
                            </div>

                            <div className="plan-content">
                                <h2 className="plan-name">{plan.name}</h2>
                                <p className="plan-description">
                                    For <span className="highlight">{plan.targetAudience}</span> {plan.description}
                                </p>

                                {plan.originalPrice && !isInternational && (
                                    <div className="discount-container">
                                        <span className="original-price">₹{plan.originalPrice.toLocaleString('en-IN')}</span>
                                        {plan.discountLabel && (
                                            <span className="discount-badge">{plan.discountLabel}</span>
                                        )}
                                    </div>
                                )}
                                <div className="plan-price">
                                    <span className="price-currency">{getPrice(plan.id, plan.price).currency}</span>
                                    <span className="price-amount">
                                        {plan.id === 'complete' && !isInternational ? '3,999' : getPrice(plan.id, plan.price).display}
                                    </span>
                                    <span className="price-period">
                                        {plan.id === 'complete' ? (isInternational ? '/month' : 'first month') : 'one-time'}
                                    </span>
                                </div>

                                {plan.id === 'complete' && !isInternational && (
                                    <div className="price-subtext-container">
                                        <p className="price-note">Then ₹2,499/month</p>
                                        <span className="cancel-anytime-badge">Cancel anytime</span>
                                    </div>
                                )}



                                <p className="includes-label">
                                    Includes:
                                </p>

                                <ul className="features-list main-features">
                                    {plan.mainFeatures
                                        .filter(feature => !isInternational || !feature.text.includes('100+ labs'))
                                        .map((feature, i) => (
                                            <li key={i} className="feature-item">
                                                <span className="feature-icon colored">{Icons[feature.iconKey](iconColors[feature.iconKey])}</span>
                                                <div className="feature-text-wrap">
                                                    <span className="feature-title">{feature.text}</span>
                                                    {feature.subtext && <span className="feature-subtext">{feature.subtext}</span>}
                                                </div>
                                            </li>
                                        ))}
                                    {/* Ayurvedic feature for all international users */}
                                    {isInternational && (
                                        <li className="feature-item">
                                            <span className="feature-icon colored">{Icons.pill('#10B981')}</span>
                                            <div className="feature-text-wrap">
                                                <span className="feature-title">Personalized Ayurvedic Wellness Recommendations</span>
                                                <span className="feature-subtext">Ancient wisdom meets modern science</span>
                                            </div>
                                        </li>
                                    )}
                                    {/* 1-on-1 Ayurvedic Consultation for Pro/Ultimate international users */}
                                    {isInternational && (plan.id === 'performance' || plan.id === 'complete') && (
                                        <li className="feature-item">
                                            <span className="feature-icon colored">{Icons.message('#8B5CF6')}</span>
                                            <div className="feature-text-wrap">
                                                <span className="feature-title">1-on-1 Ayurvedic Lifestyle Consultation</span>
                                                <span className="feature-subtext">Personalized guidance from certified practitioners</span>
                                            </div>
                                        </li>
                                    )}
                                </ul>

                                {plan.id !== 'complete' && (
                                    <>
                                        {(plan.id === 'performance' || plan.id === 'baseline') && <p className="still-includes-label">Also includes:</p>}
                                        <ul className="features-list included-features">
                                            {plan.includedFeatures.map((feature, i) => (
                                                <li key={i} className="feature-item small">
                                                    <span className="feature-icon colored small">{Icons[feature.iconKey](iconColors[feature.iconKey])}</span>
                                                    <span className="feature-text-small">{feature.text}</span>
                                                </li>
                                            ))}
                                            {/* Extra features for international Baseline users */}
                                            {isInternational && plan.id === 'baseline' && (
                                                <>
                                                    <li className="feature-item small">
                                                        <span className="feature-icon colored small">{Icons.screen(iconColors.screen)}</span>
                                                        <span className="feature-text-small">Access to Member portal</span>
                                                    </li>
                                                    <li className="feature-item small">
                                                        <span className="feature-icon colored small">{Icons.clipboard(iconColors.clipboard)}</span>
                                                        <span className="feature-text-small">Lifestyle, Medical History, & Family History Guided Assessments</span>
                                                    </li>
                                                </>
                                            )}
                                        </ul>
                                    </>
                                )}

                                {plan.id === 'complete' && (
                                    <>
                                        <p className="still-includes-label">Also includes:</p>
                                        <ul className="features-list included-features">
                                            {plan.includedFeatures.map((feature, i) => (
                                                <li key={i} className="feature-item small">
                                                    <span className="feature-icon colored small">{Icons[feature.iconKey](iconColors[feature.iconKey])}</span>
                                                    <span className="feature-text-small">{feature.text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                {isInternational && (
                                    <>
                                        <p className="includes-label lab-data-label" style={{ marginTop: '24px' }}>
                                            Works with your recent lab data:
                                        </p>
                                        <ul className="features-list prerequisite-list">
                                            <li className="feature-item">
                                                <span className="feature-icon colored">{Icons.folder('#6366F1')}</span>
                                                <div className="feature-text-wrap">
                                                    <span className="feature-title">Upload from any major lab</span>
                                                    <span className="feature-subtext">Quest, LabCorp, and more — results from the past 2 months</span>
                                                </div>
                                            </li>
                                        </ul>
                                        <a href="/lab-guide" target="_blank" className="lab-guide-link">
                                            Don't have a recent report? Refer our Lab Guide →
                                        </a>
                                    </>
                                )}
                            </div>

                            <button
                                className={`btn btn-full choose-btn ${plan.featured ? 'btn-accent' : 'btn-primary'}`}
                                onClick={() => onSelectPlan(exportablePlans.find(p => p.id === plan.id)!)}
                            >
                                Choose {plan.name}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div >
    );
}
